import {
  DESTINOS_DE_VIAJE,
  ESTADOS_DE_VIAJE,
  ETIQUETA_CAMPO_VIAJE,
  ETIQUETA_CAMPO_VIAJE_EN,
  ETIQUETA_DESTINO_VIAJE,
  ETIQUETA_DESTINO_VIAJE_EN,
  ETIQUETA_ESTADO_VIAJE,
  ETIQUETA_ESTADO_VIAJE_EN,
  type CampoDeViaje,
  type CamposDeViaje,
  type Viaje,
} from '@cnc/contracts';
import {useState, type FormEvent} from 'react';
import {ErrorDeApi} from '../api/cliente';
import {useCrearViaje, useEditarViaje} from '../api/transporte';
import {et, tr} from '../i18n/idioma';
import {AYUDA_VIAJE, ayudaDeViaje} from './ayuda-inventario';
import {NotaDeAyuda} from './nota-de-ayuda';

type Borrador = Record<CampoDeViaje, string>;

const desde = (v: Viaje | null): Borrador => ({
  salida: v?.salida ?? '',
  descarga: v?.descarga ?? '',
  tipoVehiculo: v?.tipoVehiculo ?? '',
  contenedorTerrestre: v?.contenedorTerrestre ?? '',
  toneladas: v?.toneladas === null || v?.toneladas === undefined ? '' : String(v.toneladas),
  transportista: v?.transportista ?? '',
  destino: v?.destino ?? '',
  estado: v?.estado ?? 'realizado',
  motivoCancelacion: v?.motivoCancelacion ?? '',
  notas: v?.notas ?? '',
  placa: v?.placa ?? '',
  cedula: v?.cedula ?? '',
  conductor: v?.conductor ?? '',
});

const aValor = (campo: CampoDeViaje, texto: string): CamposDeViaje[CampoDeViaje] => {
  const t = texto.trim();
  if (campo === 'estado') return (t || 'realizado') as CamposDeViaje['estado'];
  if (!t) return null;
  if (campo === 'toneladas') return Number(t.replace(',', '.'));
  return t as never;
};

// Formulario de un viaje: editar (con su version) o agregar uno a una
// recoleccion. Conductor, cedula y placa solo aparecen para administradores.
export function EditorDeViaje({
  viaje,
  personales,
  transportistas,
  onListo,
}: {
  viaje: Viaje | null;
  personales: boolean;
  transportistas: string[];
  onListo: () => void;
}) {
  const [borrador, setBorrador] = useState<Borrador>(() => desde(viaje));
  const [recoleccion, setRecoleccion] = useState('');
  const [error, setError] = useState<string | null>(null);
  const editar = useEditarViaje();
  const crear = useCrearViaje();
  const etiqueta = et(ETIQUETA_CAMPO_VIAJE, ETIQUETA_CAMPO_VIAJE_EN);
  const destinos = et(ETIQUETA_DESTINO_VIAJE, ETIQUETA_DESTINO_VIAJE_EN);
  const estados = et(ETIQUETA_ESTADO_VIAJE, ETIQUETA_ESTADO_VIAJE_EN);
  const original = desde(viaje);
  const poner = (campo: CampoDeViaje, valor: string) => setBorrador(b => ({...b, [campo]: valor}));

  const guardar = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    const campos = (Object.keys(borrador) as CampoDeViaje[]).filter(
      c => personales || !['placa', 'cedula', 'conductor'].includes(c),
    );
    try {
      if (viaje) {
        const cambios = Object.fromEntries(
          campos.filter(c => borrador[c] !== original[c]).map(c => [c, aValor(c, borrador[c])]),
        );
        if (Object.keys(cambios).length)
          await editar.mutateAsync({id: viaje.id, cuerpo: {version: viaje.version, cambios}});
      } else {
        const valores = Object.fromEntries(
          campos.filter(c => borrador[c].trim()).map(c => [c, aValor(c, borrador[c])]),
        );
        await crear.mutateAsync({recoleccion: recoleccion.trim().toUpperCase(), ...valores});
      }
      onListo();
    } catch (err) {
      setError(err instanceof ErrorDeApi ? err.message : tr('No se pudo guardar.', 'Could not save.'));
    }
  };

  const entrada = (campo: CampoDeViaje, tipo: 'text' | 'date' | 'number' = 'text', ancho = 'w-40') => (
    <label className="flex flex-col gap-1 text-xs">
      <span className="text-apagado">
        {etiqueta[campo]}
        {ayudaDeViaje(campo) && <NotaDeAyuda titulo={etiqueta[campo]} texto={ayudaDeViaje(campo)!} dentroDeEtiqueta />}
      </span>
      <input
        type={tipo}
        step={tipo === 'number' ? '0.01' : undefined}
        value={borrador[campo]}
        onChange={ev => poner(campo, ev.target.value)}
        list={campo === 'transportista' ? 'transportistas-de-viajes' : undefined}
        className={`${ancho} rounded-md border border-linea px-2 py-1 text-sm`}
      />
    </label>
  );
  const ocupado = editar.isPending || crear.isPending;

  return (
    <form onSubmit={e => void guardar(e)} className="space-y-3 bg-papel/60 p-3" data-prueba="editor-de-viaje">
      <div className="flex flex-wrap items-end gap-3">
        {!viaje && (
          <label className="flex flex-col gap-1 text-xs">
            <span className="text-apagado">
              {tr('Recolección (S-###)', 'Pickup (S-###)')}
              <NotaDeAyuda texto={AYUDA_VIAJE.recoleccion} dentroDeEtiqueta />
            </span>
            <input
              required
              value={recoleccion}
              onChange={ev => setRecoleccion(ev.target.value)}
              className="w-28 rounded-md border border-linea px-2 py-1 text-sm"
            />
          </label>
        )}
        {entrada('salida', 'date')}
        {entrada('descarga', 'date')}
        {entrada('toneladas', 'number', 'w-24')}
        {entrada('transportista')}
        <datalist id="transportistas-de-viajes">
          {transportistas.map(x => (
            <option key={x} value={x} />
          ))}
        </datalist>
        <label className="flex flex-col gap-1 text-xs">
          <span className="text-apagado">
            {etiqueta.destino}
            <NotaDeAyuda titulo={etiqueta.destino} texto={AYUDA_VIAJE.destino} dentroDeEtiqueta />
          </span>
          <select
            value={borrador.destino}
            onChange={ev => poner('destino', ev.target.value)}
            className="rounded-md border border-linea bg-white px-2 py-1 text-sm"
          >
            <option value="">—</option>
            {DESTINOS_DE_VIAJE.map(d => (
              <option key={d} value={d}>
                {destinos[d]}
              </option>
            ))}
          </select>
        </label>
        {entrada('tipoVehiculo', 'text', 'w-24')}
        {entrada('contenedorTerrestre', 'text', 'w-36')}
      </div>
      <div className="flex flex-wrap items-end gap-3">
        {personales && (
          <>
            {entrada('conductor', 'text', 'w-56')}
            {entrada('cedula', 'text', 'w-32')}
            {entrada('placa', 'text', 'w-24')}
          </>
        )}
        <label className="flex flex-col gap-1 text-xs">
          <span className="text-apagado">
            {etiqueta.estado}
            <NotaDeAyuda titulo={etiqueta.estado} texto={AYUDA_VIAJE.estado} dentroDeEtiqueta />
          </span>
          <select
            value={borrador.estado}
            onChange={ev => poner('estado', ev.target.value)}
            className="rounded-md border border-linea bg-white px-2 py-1 text-sm"
          >
            {ESTADOS_DE_VIAJE.map(x => (
              <option key={x} value={x}>
                {estados[x]}
              </option>
            ))}
          </select>
        </label>
        {borrador.estado === 'cancelado' && entrada('motivoCancelacion', 'text', 'w-56')}
        {entrada('notas', 'text', 'w-72')}
      </div>
      {error && (
        <p className="text-sm text-alerta" role="alert">
          {error}
        </p>
      )}
      <div className="flex gap-2">
        <button
          type="submit"
          disabled={ocupado}
          className="rounded-md bg-verde px-3 py-1.5 text-sm font-medium text-white hover:bg-verde-oscuro disabled:opacity-50"
        >
          {ocupado ? tr('Guardando…', 'Saving…') : viaje ? tr('Guardar', 'Save') : tr('Agregar viaje', 'Add trip')}
        </button>
        <button type="button" onClick={onListo} className="rounded-md border border-linea bg-white px-3 py-1.5 text-sm">
          {tr('Cancelar', 'Cancel')}
        </button>
      </div>
    </form>
  );
}
